import * as React from "react";
import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { fmtDate, fmtThroughInclusive } from "../utils/eligibilityDisplay";
import { rejectEligibilityPeriod } from "../services/eligibilityApi";

// Explicit confirmation dialog for rejecting an eligibility proposal. Shows the franchise/com code,
// the interval, and the evidence/HELD reason, and states that rejection keeps the proposal
// non-participating. On success calls onRejected(); on failure keeps the dialog open with the
// backend error. The backend is authoritative about whether the transition is valid.
export default function EligibilityRejectDialog({ open, period, comCode, franchiseName, onClose, onRejected }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!period) return null;

  const throughInclusive = fmtThroughInclusive(period.enabled_through_date);

  const handleReject = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await rejectEligibilityPeriod(period.enablement_period_id);
      onRejected();
    } catch (e) {
      setError(e.message || "Reject failed.");
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject eligibility proposal</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>{comCode}</strong>
          {franchiseName ? ` · ${franchiseName}` : ""}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Enabled From</Typography>
          <Typography variant="body2">{fmtDate(period.enabled_from_date)}</Typography>
          <Typography variant="body2" color="text.secondary">Enabled Through</Typography>
          <Typography variant="body2">{throughInclusive ?? "Open-ended"}</Typography>
          <Typography variant="body2" color="text.secondary">Evidence / reason</Typography>
          <Typography variant="body2">{period.evidence_note || "—"}</Typography>
        </Box>
        <Alert severity="warning" sx={{ mb: error ? 1.5 : 0 }}>
          Rejecting this proposal records your review decision and keeps it{" "}
          <strong>non-participating</strong> in the Season Audit. The row stays visible in the
          timeline as historical configuration. This does not delete anything or change dates.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleReject} color="error" variant="contained" disabled={submitting}>
          {submitting ? "Rejecting…" : "Reject proposal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
